import React, { useCallback, useEffect, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, Archive, Settings2, ShieldAlert, AlertTriangle,
         CheckCircle2, Save } from "lucide-react";

// Le tre figure che il datore di lavoro designa. L'RLS non c'e': è eletto dai
// lavoratori, non nominato, e infatti il backend non gli dà un modulo.
const FIGURE = [
  { codice: "primo_soccorso", nome: "Primo Soccorso",
    nota: "D.M. 388/2003 · aggiornamento almeno triennale" },
  { codice: "antincendio", nome: "Antincendio",
    nota: "D.M. 2 settembre 2021 · aggiornamento almeno quinquennale" },
  { codice: "preposto", nome: "Preposto",
    nota: "art. 18 c.1 lett. b-bis) e art. 19 · aggiornamento almeno biennale" },
];

const CAMPI_DATI = [
  { k: "legale_rappresentante", label: "Legale rappresentante" },
  { k: "ragione_sociale", label: "Ragione sociale" },
  { k: "sede", label: "Sede legale" },
  // L'indirizzo che i moduli stampano in intestazione e' questo, non la sede
  // legale: e' la sede da cui si esce per andare in cantiere.
  { k: "sede_operativa", label: "Sede operativa (è quella stampata sui moduli)" },
  { k: "telefono", label: "Telefono" },
  { k: "rls", label: "RLS consultato" },
  { k: "medico_competente", label: "Medico competente" },
  { k: "gruppo_primo_soccorso", label: "Gruppo primo soccorso (D.M. 388/03)" },
  { k: "livello_antincendio", label: "Livello corso antincendio" },
  { k: "luogo", label: "Luogo in calce" },
];

export default function NomineSicurezza() {
  const { user } = useAuth();
  const [dati, setDati] = useState(null);
  const [bozza, setBozza] = useState(null);
  const [mostraDati, setMostraDati] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [perFigura, setPerFigura] = useState({});
  const [scelti, setScelti] = useState({});
  const [data, setData] = useState(new Date().toISOString().substring(0, 10));
  const [occupato, setOccupato] = useState(null);

  const carica = useCallback(async () => {
    const { data: d } = await api.get("/settings/nomine");
    setDati(d); setBozza(d);
    const risultati = {};
    const preselezione = {};
    for (const f of FIGURE) {
      try {
        const { data: elenco } = await api.get(`/safety-figures/${f.codice}/designabili`);
        risultati[f.codice] = elenco;
        // Parte selezionato chi ha l'attestato valido: è quello che il modulo
        // dichiara. Gli scaduti restano visibili ma da spuntare a mano.
        preselezione[f.codice] = elenco.filter(p => p.valido).map(p => p.id);
      } catch { risultati[f.codice] = []; preselezione[f.codice] = []; }
    }
    setPerFigura(risultati); setScelti(preselezione);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salvaDati = async () => {
    setSalvando(true);
    try {
      const { data: d } = await api.put("/settings/nomine", bozza);
      setDati(d); setBozza(d);
      toast.success("Dati aggiornati");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSalvando(false); }
  };

  const spunta = (codice, id) => setScelti(prev => {
    const attuali = prev[codice] || [];
    return { ...prev, [codice]: attuali.includes(id)
      ? attuali.filter(x => x !== id) : [...attuali, id] };
  });

  const scarica = async (f) => {
    const ids = scelti[f.codice] || [];
    if (ids.length === 0) { toast.error("Seleziona almeno un lavoratore"); return; }
    setOccupato(`${f.codice}-pdf`);
    try {
      const r = await authFetch(
        `/safety-figures/${f.codice}/incarico/pdf?employee_ids=${ids.join(",")}&data=${data}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "");
      }
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = `nomina_${f.codice}_${data}.pdf`; a.click();
      window.URL.revokeObjectURL(u);
    } catch (e) {
      toast.error(e.message || "Errore nella generazione del modulo");
    } finally { setOccupato(null); }
  };

  const archivia = async (f) => {
    const ids = scelti[f.codice] || [];
    if (ids.length === 0) { toast.error("Seleziona almeno un lavoratore"); return; }
    setOccupato(`${f.codice}-arch`);
    try {
      const fd = new FormData();
      fd.append("employee_ids", ids.join(","));
      fd.append("data", data);
      await api.post(`/safety-figures/${f.codice}/incarico/archivia`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Modulo archiviato fra i documenti aziendali");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'archiviazione");
    } finally { setOccupato(null); }
  };

  return (
    <div className="space-y-6" data-testid="nomine-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#0067b2] mb-2">Dipendenti</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Nomine e incarichi sicurezza</h1>
        <p className="text-sm text-slate-500 mt-1">
          I moduli di designazione compilati con chi ha l'attestato in corso di validità.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 text-xs text-amber-900 flex gap-2">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Il PDF è una <strong>bozza da stampare e far sottoscrivere</strong>: vale quando
            è firmato in originale dal datore di lavoro e da ciascun designato, con copia
            consegnata all'interessato. I riferimenti normativi sono aggiornati rispetto ai
            modelli cartacei in uso — falli comunque validare dal vostro RSPP prima del
            primo utilizzo.
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <button type="button" onClick={() => setMostraDati(v => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-700"
                  data-testid="mostra-dati-nomine">
            <Settings2 className="w-4 h-4 text-[#0067b2]" />
            Dati che compaiono in intestazione
            <span className="text-xs font-normal text-slate-400">
              ({dati?.totale_addetti ?? "—"} addetti in forza, contati dai fascicoli attivi)
            </span>
          </button>
          {mostraDati && bozza && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CAMPI_DATI.map(c => (
                  <div key={c.k}>
                    <Label className="text-xs">{c.label}</Label>
                    <Input value={bozza[c.k] || ""} disabled={user?.role !== "admin"}
                           onChange={(e) => setBozza({ ...bozza, [c.k]: e.target.value })}
                           data-testid={`dato-${c.k}`} className="mt-1" />
                  </div>
                ))}
              </div>
              {user?.role === "admin" && (
                <Button onClick={salvaDati} disabled={salvando}
                        className="bg-[#0067b2] hover:bg-[#00559a] text-white"
                        data-testid="salva-dati-nomine">
                  <Save className="w-4 h-4 mr-2" /> {salvando ? "Salvataggio..." : "Salva"}
                </Button>
              )}
              {!dati?.rls && (
                <p className="text-xs text-amber-700">
                  Senza il nome dell'RLS i moduli di primo soccorso e antincendio non
                  riportano la consultazione prevista dall'art. 50, comma 1, lettera b
                  del D.Lgs. 81/08.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Data della nomina</Label>
          <Input type="date" value={data} onChange={(e) => setData(e.target.value)}
                 className="mt-1 w-48" data-testid="data-nomina" />
        </div>
      </div>

      <div className="space-y-4">
        {FIGURE.map(f => {
          const elenco = perFigura[f.codice] || [];
          const selezionati = scelti[f.codice] || [];
          const scaduti = elenco.filter(p => !p.valido).length;
          return (
            <Card key={f.codice} className="border-slate-200" data-testid={`figura-${f.codice}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display text-xl font-bold">{f.nome}</div>
                    <div className="text-xs text-slate-500">{f.nota}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-100 text-slate-700 border-0">
                      {selezionati.length} selezionat{selezionati.length === 1 ? "o" : "i"}
                    </Badge>
                    {scaduti > 0 && (
                      <Badge className="bg-amber-100 text-amber-800 border-0">
                        <AlertTriangle className="w-3 h-3 mr-1" /> {scaduti} con attestato scaduto
                      </Badge>
                    )}
                  </div>
                </div>

                {elenco.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">
                    Nessun dipendente ha in archivio un attestato per questa figura.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {elenco.map(p => {
                      const spuntato = selezionati.includes(p.id);
                      return (
                        <label key={p.id}
                               data-testid={`scelta-${f.codice}-${p.id}`}
                               className={`flex items-start gap-2 border rounded p-2 cursor-pointer text-sm transition-colors ${
                                 spuntato ? "border-[#0067b2] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                          <input type="checkbox" checked={spuntato} className="mt-0.5"
                                 onChange={() => spunta(f.codice, p.id)} />
                          <span className="min-w-0">
                            <span className="font-medium block truncate">{p.full_name}</span>
                            <span className={`text-xs ${p.valido ? "text-slate-500" : "text-amber-700"}`}>
                              {p.valido
                                ? <><CheckCircle2 className="w-3 h-3 inline mr-1" />
                                    {p.expires_date ? `valido fino al ${p.expires_date}` : "senza scadenza"}</>
                                : <><AlertTriangle className="w-3 h-3 inline mr-1" />
                                    scaduto il {p.expires_date}</>}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap pt-1">
                  <Button onClick={() => scarica(f)} disabled={occupato === `${f.codice}-pdf`}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid={`scarica-${f.codice}`}>
                    <Download className="w-4 h-4 mr-2" />
                    {occupato === `${f.codice}-pdf` ? "Generazione..." : "Scarica il modulo"}
                  </Button>
                  <Button variant="outline" onClick={() => archivia(f)}
                          disabled={occupato === `${f.codice}-arch`}
                          data-testid={`archivia-${f.codice}`}>
                    <Archive className="w-4 h-4 mr-2" />
                    {occupato === `${f.codice}-arch` ? "Archiviazione..." : "Archivia in documenti aziendali"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
